import {
  Appointment,
  AppointmentInput,
  AppointmentSummary,
  PaginatedAppointments
} from 'types/AppointmentsTypes';
import useHttp, { useHttpWithoutRedirect } from '../helpers/HttpMethods';

const http = useHttp();
const httpNoRedirect = useHttpWithoutRedirect();

class AppointmentsService {
  /**
   * Busca os apontamentos de um ticket paginados.
   * @param {number} id - ID do ticket.
   * @param {string} filters - Filtros em string.
   * @returns {Promise<PaginatedAppointments | Response>} - Apontamentos do ticket em formato paginado.
   */
  static async list(
    id: number,
    filters: string
  ): Promise<PaginatedAppointments | Response> {
    return await http.get(`/tickets/${id}/appointments?${filters}`);
  }

  /**
   * Busca o resumo dos apontamentos de um ticket.
   * @param {number} id - ID do ticket.
   * @returns {Promise<AppointmentSummary | Response>} - Resumo dos apontamentos do ticket.
   */
  static async summary(id: number): Promise<AppointmentSummary | Response> {
    return await http.get(`/tickets/${id}/appointments/summary`);
  }

  /**
   * Cria um apontamento para um ticket.
   * @param {number} ticketId - ID do ticket.
   * @param {AppointmentInput} body - Dados do apontamento.
   * @returns {Promise<Appointment | Response>} - Apontamento criado ou resposta de erro.
   */
  static async create(
    ticketId: number,
    body: AppointmentInput
  ): Promise<Appointment | Response> {
    return await httpNoRedirect.post(body, `/tickets/${ticketId}/appointments`);
  }

  /**
   * Cria um apontamento e pausa o ticket.
   * @param {number} ticketId - ID do ticket.
   * @param {AppointmentInput} body - Dados do apontamento.
   * @returns {Promise<Appointment | Response>} - Apontamento criado ou resposta de erro.
   */
  static async createAndPause(
    ticketId: number,
    body: AppointmentInput
  ): Promise<Appointment | Response> {
    return await httpNoRedirect.post(
      body,
      `/tickets/${ticketId}/appointments/add-and-pause`
    );
  }

  /**
   * Edita um apontamento de um ticket.
   * @param {number} ticketId - ID do ticket.
   * @param {number} appointmentId - ID do apontamento.
   * @param {AppointmentInput} body - Dados do apontamento para atualização.
   * @returns {Promise<Appointment | Response>} - Apontamento atualizado ou resposta de erro.
   */
  static async edit(
    ticketId: number,
    appointmentId: number,
    body: AppointmentInput
  ): Promise<Appointment | Response> {
    return await httpNoRedirect.put(
      body,
      `/tickets/${ticketId}/appointments/${appointmentId}`
    );
  }

  /**
   * Remove um apontamento de um ticket.
   * @param {number} ticketId - ID do ticket.
   * @param {number} appointmentId - ID do apontamento.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async delete(
    ticketId: number,
    appointmentId: number
  ): Promise<Response> {
    return await http.del(`/tickets/${ticketId}/appointments/${appointmentId}`);
  }
}

export default AppointmentsService;
